import { FC, useCallback } from 'react';

import useTranslations from 'modules/I18n/hooks/useTranslations';
import { useCloseModal } from 'modules/Modals/hooks';
import useTheme from 'modules/Theme/hooks/useTheme';
import { Button, Hr, Text } from 'modules/Ui';

import { dialogConfim, dialogVerifactuConfirm } from '../messages';
import { DialogBaseProps } from '../models/types';
import { Dialog } from './Dialog';

interface DialogConfirmProps extends DialogBaseProps {
  onConfirm(close: () => void): void;
  onCancel?(): void;
  onCloseClick?(): void;
}

export const VerifactuDialogConfirm: FC<DialogConfirmProps> = ({
  id,
  onCancel,
  onCloseClick,
  onConfirm,
}) => {
  const closeModal = useCloseModal();
  const { t } = useTranslations();
  const theme = useTheme();

  const handleCancel = useCallback(() => {
    onCancel?.();
    closeModal();
  }, [closeModal, onCancel]);

  const handleConfirm = useCallback(() => {
    onConfirm(() => {
      closeModal();
    });
  }, [closeModal, onConfirm]);

  const handleOnClose = useCallback(() => {
    onCloseClick?.();
    closeModal();
  }, [closeModal, onCloseClick]);

  return (
    <Dialog
      onClose={handleOnClose}
      id={id}
      title={t(dialogVerifactuConfirm.title)}
      buttonPrimary={
        <Button
          data-testid={`${id}-submit`}
          variant="secondary"
          onClick={handleConfirm}
        >
          {t(dialogVerifactuConfirm.btnConfirm)}
        </Button>
      }
      buttonSecondary={
        <Button
          data-testid={`${id}-cancel`}
          onClick={handleCancel}
          variant="stroke"
        >
          {t(dialogConfim.cancel)}
        </Button>
      }
    >
      <>
        <Text
          fontWeight="bold"
          marginBottom="16px"
          lineHeight={22}
          color="gray800"
        >
          {t(dialogVerifactuConfirm.subtitle)}
        </Text>
        <Text marginBottom="16px" lineHeight={22} color="gray800">
          {t(dialogVerifactuConfirm.text1)}
        </Text>
        <Text marginBottom="24px" lineHeight={22} color="gray800">
          {t(dialogVerifactuConfirm.text2)}
        </Text>
        <Hr marginBottom="0" color={theme.colors.primary100} />
      </>
    </Dialog>
  );
};
